import { useState } from "react";
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { CirclePause, Play } from "lucide-react";
import type { Stage } from "@amber/contracts";
import {
  AppLink,
  Button,
  Callout,
  DataTable,
  Dialog,
  DialogFooter,
  DialogText,
  FailedCallout,
  FieldError,
  LinkButton,
  Overline,
  ReasonDialog,
  ReasonField,
  ReadOnlyNote,
  SectionHeader,
  Status,
  dataColumns,
  errorText,
  formatDateTime,
  isApiError,
  useCommandMutation,
  useReason,
  useToast,
  type DataColumn,
} from "@amber/ui";
import {
  OPERATIONS_ACTIONS,
  STAGE_LABELS,
  type PauseView,
  type PipelineResponse,
  type ResumePreview,
} from "../../contracts/index";
import { operationsCommands, pausesQuery, pipelineQuery, resumePreviewQuery } from "../queries";
import { AFTER_OPERATIONS_CHANGE, AFTER_OPERATIONS_CONFLICT } from "../keys";
import { waitingFor } from "../shared";

function itemsWaiting(n: number): string {
  return n === 1 ? "1 item waits" : `${String(n)} items wait`;
}

export function PauseBanner() {
  const query = useQuery(pausesQuery);
  const [resuming, setResuming] = useState<PauseView | null>(null);
  const pauses = query.data?.pauses ?? [];
  if (pauses.length === 0) return null;
  const resume = query.data?.resume;
  return (
    <div
      role="status"
      aria-label="Pipeline pauses"
      className="flex flex-none flex-col border-b border-waiting/40 bg-waiting-soft"
    >
      {pauses.map((p) => (
        <div key={p.id} className="flex min-h-[38px] items-center gap-2 px-4 text-md max-compact:px-2">
          <CirclePause size={14} strokeWidth={1.8} aria-hidden="true" className="flex-none text-waiting" />
          <p className="m-0 min-w-0 flex-1 truncate">
            <b className="font-semibold">{p.label}</b>
            <span className="text-muted">
              {" "}
              · {p.started_by}, {formatDateTime(p.started_at)} · “{p.reason}” · {itemsWaiting(p.waiting)}
              {p.level === "stage"
                ? " in front of it; earlier stages continue."
                : "; viewing and corrections continue."}
            </span>
          </p>
          <Button
            size="sm"
            disabledReason={resume && !resume.enabled ? resume.reason : null}
            onClick={() => {
              setResuming(p);
            }}
          >
            <Play aria-hidden="true" />
            Resume…
          </Button>
          <AppLink
            href="/settings/pipeline"
            className="px-1.5 font-strong text-fg no-underline hover:underline max-narrow:hidden"
          >
            Pipeline
          </AppLink>
        </div>
      ))}
      {resuming ? (
        <ResumeDialog
          pause={resuming}
          onClose={() => {
            setResuming(null);
          }}
        />
      ) : null}
    </div>
  );
}

function ResumePreviewFacts({ preview }: { preview: UseQueryResult<ResumePreview> }) {
  const p = preview.data;
  if (p) {
    return (
      <dl className="m-0 mb-1 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 rounded-lg border border-divider bg-surface px-3 py-2.5 text-md [&_dd]:m-0 [&_dt]:text-muted">
        <dt>Queued work</dt>
        <dd>{p.waiting_items === 1 ? "1 item" : `${String(p.waiting_items)} items`}</dd>
        <dt>Estimated cost</dt>
        <dd>
          USD {p.estimate.amount_decimal} <span className="text-muted">· {p.estimate_basis}</span>
        </dd>
        <dt>Throttling</dt>
        <dd>Normal; queued work re-enters in order</dd>
        {p.still_paused.length > 0 ? (
          <>
            <dt>Still paused</dt>
            <dd>{p.still_paused.join(", ")}: its work keeps waiting</dd>
          </>
        ) : null}
      </dl>
    );
  }
  if (preview.isError) {
    return (
      <Callout tone="attention" role="alert">
        Couldn&apos;t load the preview. {errorText(preview.error)}
      </Callout>
    );
  }
  return <p className="m-0 text-muted">Loading the preview…</p>;
}

export function ResumeDialog({ pause, onClose }: { pause: PauseView; onClose: () => void }) {
  const preview = useQuery(resumePreviewQuery(pause.id));
  return (
    <ReasonDialog
      title={pause.level === "global" ? "Resume everything?" : `Resume ${STAGE_LABELS[pause.stage ?? "discover"]}?`}
      confirm="Resume"
      success={
        pause.level === "global"
          ? "Everything is running again"
          : `${STAGE_LABELS[pause.stage ?? "discover"]} is running again`
      }
      onClose={onClose}
      invalidate={AFTER_OPERATIONS_CHANGE}
      invalidateOnConflict={AFTER_OPERATIONS_CONFLICT}
      ready={preview.isSuccess}
      run={(reason, csrf) => operationsCommands.resume(pause.id, reason, csrf)}
      extra={<ResumePreviewFacts preview={preview} />}
    >
      Paused since {formatDateTime(pause.started_at)} by {pause.started_by}: “{pause.reason}”. Pauses never end on their
      own.
    </ReasonDialog>
  );
}

function PauseStageDialog({ stage, onClose }: { stage: Stage; onClose: () => void }) {
  const pipeline = useQuery(pipelineQuery);
  const affected = pipeline.data?.stages.find((s) => s.stage === stage)?.waiting ?? 0;
  return (
    <ReasonDialog
      title={`Pause ${STAGE_LABELS[stage]}?`}
      confirm="Pause stage"
      success={`${STAGE_LABELS[stage]} is paused`}
      onClose={onClose}
      invalidate={AFTER_OPERATIONS_CHANGE}
      invalidateOnConflict={AFTER_OPERATIONS_CONFLICT}
      run={(reason, csrf) => operationsCommands.pause({ level: "stage", stage, reason }, csrf)}
    >
      The stage stops for all items; earlier stages continue. Items queue in front of it and show as Waiting with your
      reason. Attempt budgets aren&apos;t used while paused, and the pause has no automatic end.{" "}
      {affected === 1 ? "1 item is" : `${String(affected)} items are`} waiting in front of it now.
    </ReasonDialog>
  );
}

function PauseAllDialog({ onClose }: { onClose: () => void }) {
  const toast = useToast();
  const [acknowledged, setAcknowledged] = useState(false);
  const pipeline = useQuery(pipelineQuery);
  const mutation = useCommandMutation({
    run: (csrf, text: string) => operationsCommands.pause({ level: "global", reason: text, acknowledged: true }, csrf),
    invalidate: AFTER_OPERATIONS_CHANGE,
    invalidateOnConflict: AFTER_OPERATIONS_CONFLICT,
    onSuccess: () => {
      toast.show("Everything is paused");
      onClose();
    },
  });
  const fieldErrors = isApiError(mutation.error) ? mutation.error.fieldErrors() : {};
  const reason = useReason(fieldErrors["reason"]);
  const submitted = reason.submitted;
  const expiring = pipeline.data?.expiring_originals ?? 0;
  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title="Pause everything?"
      onSubmit={() => {
        if (reason.submit() && acknowledged) mutation.mutate(reason.trimmed);
      }}
      footer={
        <DialogFooter
          onClose={onClose}
          confirm="Pause everything"
          danger
          pending={mutation.isPending}
          failed={mutation.isError}
        />
      }
    >
      <DialogText>
        Discovery, download, archiving and processing stop for every source, with no exemptions. Viewing and corrections
        continue. Nothing ends the pause automatically.
      </DialogText>
      <p className="m-0 mb-2 text-md text-muted">
        {expiring === 0
          ? "No known-expiring originals are waiting to be archived."
          : `${String(expiring)} known-expiring originals may become unavailable while paused.`}
      </p>
      <label className="mt-2 flex items-start gap-2 text-md">
        <input
          type="checkbox"
          className="mt-0.5"
          checked={acknowledged}
          aria-invalid={submitted && !acknowledged ? true : undefined}
          aria-describedby={submitted && !acknowledged ? "ack-err" : undefined}
          onChange={(e) => {
            setAcknowledged(e.target.checked);
          }}
        />
        I understand that nothing new is collected until someone resumes.
      </label>
      {submitted && !acknowledged ? <FieldError id="ack-err">Confirm that you understand what stops</FieldError> : null}
      <ReasonField value={reason.reason} onChange={reason.setReason} error={reason.error} />
      <FailedCallout error={mutation.error} fieldsShown />
    </Dialog>
  );
}

const STATE_TEXT = { running: "Running", paused: "Paused", held_by_global: "Held by global pause" } as const;

type StageRow = PipelineResponse["stages"][number];

const col = dataColumns<StageRow>();

function stageColumns(
  data: PipelineResponse | undefined,
  reasonOf: (action: string) => string | null,
  setDialog: (dialog: PipelineDialog) => void,
): DataColumn<StageRow>[] {
  return [
    col.display({
      id: "stage",
      header: "Stage",
      meta: { width: 200, cellClassName: "font-strong text-fg" },
      cell: ({ row: { original: s } }) => (
        <>
          {s.label}
          {s.stage === "transcribe" ? (
            <span className="ml-1.5 text-md font-normal text-muted">or Extract text</span>
          ) : null}
        </>
      ),
    }),
    col.display({
      id: "state",
      header: "State",
      meta: { width: 200 },
      cell: ({ row: { original: s } }) => (
        <Status tone={s.state === "running" ? "running" : "waiting"}>{STATE_TEXT[s.state]}</Status>
      ),
    }),
    col.display({
      id: "waiting",
      header: "Waiting",
      meta: { width: 120, cellClassName: "font-mono text-fg" },
      cell: ({ row }) => row.original.waiting,
    }),
    col.display({
      id: "oldest",
      header: "Oldest waiting",
      meta: { width: 140 },
      cell: ({ row: { original: s } }) => (s.oldest_waiting_at ? waitingFor(s.oldest_waiting_at) : "—"),
    }),
    col.display({
      id: "actions",
      meta: { label: "Actions", width: 140, cellClassName: "overflow-visible text-right" },
      cell: ({ row: { original: s } }) => {
        const own = data?.pauses.find((p) => p.id === s.pause_id);
        return own ? (
          <Button
            size="sm"
            disabledReason={reasonOf(OPERATIONS_ACTIONS.resume)}
            onClick={() => {
              setDialog({ kind: "resume", pause: own });
            }}
          >
            <Play aria-hidden="true" />
            Resume…
          </Button>
        ) : (
          <Button
            size="sm"
            disabledReason={reasonOf(OPERATIONS_ACTIONS.pauseStage)}
            aria-label={`Pause ${s.label}…`}
            onClick={() => {
              setDialog({ kind: "pauseStage", stage: s.stage });
            }}
          >
            <CirclePause aria-hidden="true" />
            Pause…
          </Button>
        );
      },
    }),
  ];
}

function ActivePause({
  pause: p,
  resumeReason,
  onResume,
}: {
  pause: PauseView;
  resumeReason: string | null;
  onResume: () => void;
}) {
  const items = p.waiting === 1 ? "1 item" : `${String(p.waiting)} items`;
  return (
    <li className="flex flex-wrap items-start gap-3 rounded-lg border border-waiting/45 bg-waiting-soft px-3.5 py-3">
      <CirclePause size={16} strokeWidth={1.8} aria-hidden="true" className="mt-0.5 flex-none text-waiting" />
      <div className="min-w-0 flex-1">
        <div className="font-semibold">{p.label}</div>
        <div className="text-md text-muted">
          {p.started_by} · {formatDateTime(p.started_at)} · no automatic end
        </div>
        <div className="mt-1">“{p.reason}”</div>
        <div className="mt-0.5 text-md text-muted">
          {p.level === "stage"
            ? `${items} waiting in front of it; shown as Waiting with this reason. Attempt budgets aren't used while paused.`
            : `${items} waiting; nothing is discovered, downloaded or processed.`}
        </div>
      </div>
      <Button size="sm" disabledReason={resumeReason} onClick={onResume}>
        <Play aria-hidden="true" />
        Resume…
      </Button>
    </li>
  );
}

function PipelineMeta({ data, readOnly }: { data: PipelineResponse; readOnly: boolean }) {
  return (
    <>
      {data.last_changed ? (
        <span>
          Last changed by <b>{data.last_changed.by}</b> · {formatDateTime(data.last_changed.at)}
        </span>
      ) : (
        <span>Not changed yet</span>
      )}
      <AppLink href="/settings/audit?action=operations" className="font-strong text-link">
        Audit log ({data.audit_count})
      </AppLink>
      {readOnly ? <ReadOnlyNote>Read-only for your role · Operators and Admins can change this</ReadOnlyNote> : null}
    </>
  );
}

type PipelineDialog =
  { kind: "pauseStage"; stage: Stage } | { kind: "pauseAll" } | { kind: "resume"; pause: PauseView } | null;

export function PipelineSettings() {
  const query = useQuery(pipelineQuery);
  const [dialog, setDialog] = useState<PipelineDialog>(null);
  const data = query.data;
  const find = (action: string) => data?.actions.find((a) => a.action === action);
  const reasonOf = (action: string) => {
    const a = find(action);
    return a && !a.enabled ? a.reason : null;
  };
  const close = () => {
    setDialog(null);
  };
  return (
    <section aria-labelledby="section-title" className="flex min-h-0 flex-1 flex-col overflow-auto">
      <SectionHeader
        titleId="section-title"
        title="Pipeline"
        lead="The six processing stages, what waits in front of each, and pauses. Source pauses live in Sources; person pauses on the profile."
        meta={data ? <PipelineMeta data={data} readOnly={reasonOf(OPERATIONS_ACTIONS.pauseAll) !== null} /> : null}
        actions={
          <Button
            variant="danger"
            disabledReason={
              reasonOf(OPERATIONS_ACTIONS.pauseAll) ??
              (data?.pauses.some((p) => p.level === "global") ? "Everything is already paused" : null)
            }
            onClick={() => {
              setDialog({ kind: "pauseAll" });
            }}
          >
            <CirclePause aria-hidden="true" />
            Pause everything…
          </Button>
        }
      />
      <div className="mx-4 mb-3 rounded-xl border border-divider bg-bg px-4 py-3 max-compact:mx-2">
        <Overline className="m-0 mb-2">
          Active pauses <span className="ml-1 font-mono">{data?.pauses.length ?? ""}</span>
        </Overline>
        {data && data.pauses.length === 0 ? (
          <p className="m-0 text-muted">Nothing is paused. Every stage runs.</p>
        ) : null}
        <ul className="m-0 flex list-none flex-col gap-2 p-0">
          {data?.pauses.map((p) => (
            <ActivePause
              key={p.id}
              pause={p}
              resumeReason={reasonOf(OPERATIONS_ACTIONS.resume)}
              onResume={() => {
                setDialog({ kind: "resume", pause: p });
              }}
            />
          ))}
        </ul>
      </div>
      <div className="mx-4 mb-2 max-compact:mx-2">
        <Overline className="m-0">Stages</Overline>
      </div>
      <DataTable
        label="Stages"
        columns={stageColumns(data, reasonOf, setDialog)}
        rows={data?.stages ?? []}
        rowId={(s) => s.stage}
        minWidth={720}
        isPending={query.isPending}
        wrapClassName="mb-4 flex-none rounded-b-xl border-b"
        empty={
          query.isError ? (
            <>
              Couldn&apos;t load the pipeline. {errorText(query.error)}{" "}
              <LinkButton
                onClick={() => {
                  void query.refetch();
                }}
              >
                Retry
              </LinkButton>
            </>
          ) : null
        }
      />
      {dialog?.kind === "pauseStage" ? <PauseStageDialog stage={dialog.stage} onClose={close} /> : null}
      {dialog?.kind === "pauseAll" ? <PauseAllDialog onClose={close} /> : null}
      {dialog?.kind === "resume" ? <ResumeDialog pause={dialog.pause} onClose={close} /> : null}
    </section>
  );
}
